import { Alert, Button, Dropdown } from "@digdir/designsystemet-react";
import {
  DownloadIcon,
  FileCsvFillIcon,
  FileCsvIcon,
  InformationSquareIcon,
} from "@navikt/aksel-icons";
import { Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Microsoft365Logo from "@/assets/external-icons/excel-microsoft-365-logo.svg?react";
import ExcelWindowsLogo from "@/assets/external-icons/excel-windows-logo.svg?react";
import GoogleSpreadsheetLogo from "@/assets/external-icons/google-sheets-logo.svg?react";
import LibreOfficeLogo from "@/assets/external-icons/LibreOffice-logo.svg?react";
import MacLogo from "@/assets/external-icons/mac-logo.svg?react";
import { Page } from "@/components";
import type { FileRoutesByTo } from "@/routeTree.gen";
import { MarkdownFile } from "./MarkdownFile";

type FilSpesifikasjonProps = {
  markdownFilePublicPath: string;
  markdownFileDownloadName: string;
  exampleFileWindowsPublicPath: string;
  exampleFileMacPublicPath: string;
  exampleFileDownloadName: string;
  helpLink: keyof FileRoutesByTo;
};

export const FilSpesifikasjon = ({
  markdownFilePublicPath,
  markdownFileDownloadName,
  exampleFileWindowsPublicPath,
  exampleFileMacPublicPath,
  exampleFileDownloadName,
  helpLink,
}: FilSpesifikasjonProps) => {
  const { t } = useTranslation();
  const [markdown, setMarkdown] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    setIsLoading(true);
    fetch(markdownFilePublicPath)
      .then((res) => res.text())
      .then((text) => setMarkdown(text))
      .catch(() => setIsError(true))
      .finally(() => setIsLoading(false));
  }, [markdownFilePublicPath]);

  return (
    <Page
      withBackLink
      actionBar={[
        <Button variant="tertiary" key="veiledning" asChild>
          <Link to={helpLink}>
            <InformationSquareIcon />
            {t("pages.dokumentasjon.actions.veiledning")}
          </Link>
        </Button>,
        <Button
          asChild
          className="w-fit"
          variant="secondary"
          key={"spesifikasjon"}
        >
          <a href={markdownFilePublicPath} download={markdownFileDownloadName}>
            <DownloadIcon />
            {t("pages.dokumentasjon.actions.last-ned-filspesifikasjon")}
          </a>
        </Button>,
        <Dropdown.TriggerContext key="eksempel-fil-dropdown">
          <Dropdown.Trigger variant="secondary">
            <FileCsvIcon />
            {t("pages.dokumentasjon.actions.last-ned-eksempel-fil")}
          </Dropdown.Trigger>
          <Dropdown>
            <Dropdown.List>
              <Dropdown.Item>
                <Dropdown.Button asChild className="w-full">
                  <a
                    href={exampleFileWindowsPublicPath}
                    download={exampleFileDownloadName}
                  >
                    <ExcelWindowsLogo
                      aria-hidden
                      aria-label="Excel windows logo"
                      className="inline w-8 h-8"
                    />
                    {t(
                      "pages.dokumentasjon.actions.last-ned-eksempel-fil-for-windows",
                    )}
                  </a>
                </Dropdown.Button>
              </Dropdown.Item>
              <Dropdown.Item>
                <Dropdown.Button asChild className="w-full">
                  <a
                    href={exampleFileMacPublicPath}
                    download={exampleFileDownloadName}
                  >
                    <MacLogo
                      aria-hidden
                      aria-label="Mac logo"
                      className="inline w-8 h-8"
                    />
                    {t(
                      "pages.dokumentasjon.actions.last-ned-eksempel-fil-for-mac",
                    )}
                  </a>
                </Dropdown.Button>
              </Dropdown.Item>
              <Dropdown.Item>
                <Dropdown.Button asChild className="w-full">
                  <a
                    href={exampleFileMacPublicPath}
                    download={exampleFileDownloadName}
                  >
                    <Microsoft365Logo
                      aria-hidden
                      aria-label="Microsoft 365 excel logo"
                      className="inline w-8 h-8"
                    />
                    {t(
                      "pages.dokumentasjon.actions.last-ned-eksempel-fil-for-365",
                    )}
                  </a>
                </Dropdown.Button>
              </Dropdown.Item>
              <Dropdown.Item>
                <Dropdown.Button asChild className="w-full">
                  <a
                    href={exampleFileMacPublicPath}
                    download={exampleFileDownloadName}
                  >
                    <GoogleSpreadsheetLogo
                      aria-hidden
                      aria-label="Google regneark logo"
                      className="inline w-8 h-8"
                    />
                    {t(
                      "pages.dokumentasjon.actions.last-ned-eksempel-fil-for-google",
                    )}
                  </a>
                </Dropdown.Button>
              </Dropdown.Item>
              <Dropdown.Item>
                <Dropdown.Button asChild className="w-full">
                  <a
                    href={exampleFileMacPublicPath}
                    download={exampleFileDownloadName}
                  >
                    <LibreOfficeLogo
                      aria-hidden
                      aria-label="LibreOffice logo"
                      className="inline w-8 h-8"
                    />
                    {t(
                      "pages.dokumentasjon.actions.last-ned-eksempel-fil-for-libre",
                    )}
                  </a>
                </Dropdown.Button>
              </Dropdown.Item>
              <Dropdown.Item>
                <Dropdown.Button asChild className="w-full">
                  <a
                    href={exampleFileMacPublicPath}
                    download={exampleFileDownloadName}
                  >
                    <FileCsvFillIcon className="inline w-8 h-8" />
                    {t(
                      "pages.dokumentasjon.actions.last-ned-eksempel-fil-for-andre",
                    )}
                  </a>
                </Dropdown.Button>
              </Dropdown.Item>
            </Dropdown.List>
          </Dropdown>
        </Dropdown.TriggerContext>,
      ]}
    >
      {isLoading ? (
        <Alert data-color={"info"} className="my-4">
          {t("generisk.laster")}
        </Alert>
      ) : isError || markdown == null ? (
        <Alert data-color={"danger"} className="my-4">
          {t("generisk.feil")}
        </Alert>
      ) : (
        <MarkdownFile markdown={markdown} />
      )}
    </Page>
  );
};
